import type { GeoPoint } from './geo'
/**
 * Domain model for the Skida PRO CRM.
 *
 * These types describe CRM-owned data only. Everything the CRM reads out of the
 * existing Skida PRO collections (`users`, `skidaProCompanies`,
 * `skidaProCompanyMembers`, `billingSubscriptions`) is projected onto these
 * shapes by the repository layer / Cloud Functions — the UI never sees a raw
 * product document, and the CRM never writes to a product collection.
 */

/** A non-terminal pipeline stage. Order is significant (drives the stepper). */
export interface PipelineStage {
  key: string
  label: string
  color: string
  /** Light tint paired with `color`, used for badge/pill backgrounds. */
  bg: string
  /** Built-in stages ship with the product and are referenced by Cloud Functions. */
  builtin?: boolean
  /**
   * Kept out of the lead list unless asked for.
   *
   * A holding pen, not a pipeline position. `unverified` uses it: leads that
   * arrived without enough information to work sit there instead of padding
   * everyone's list with rows nobody can act on. Cleaning them up is its own
   * job, done deliberately — so the list has to be opted into, not opted out of.
   */
  hidden?: boolean
}

/** Terminal outcomes. Fixed as a pair — label and color are editable, the set is not. */
export interface PipelineOutcome {
  key: 'won' | 'lost'
  label: string
  color: string
  bg: string
}

export interface PipelineSettings {
  stages: PipelineStage[]
  outcomes: PipelineOutcome[]
  updatedAt?: string
  updatedBy?: string
}

/** One bilingual outreach template, keyed by stage (or Prospect awareness variant). */
export interface EmailTemplate {
  title: string
  guidance: string
  subjectNo: string
  bodyNo: string
  subjectEn: string
  bodyEn: string
}

/**
 * Template keys are stage keys, except Prospect/Lead which splits into two
 * awareness variants derived from the consumer-Skida cross-check.
 */
export type TemplateKey = 'prospect_aware' | 'prospect_unaware' | string

export interface TemplateSettings {
  templates: Record<TemplateKey, EmailTemplate>
  updatedAt?: string
  updatedBy?: string
}

export interface Contact {
  name: string
  role: string
  email: string
  phone: string
  /**
   * Manual override for the consumer-Skida cross-check, set from the contact
   * modal. The live lookup against `users` still wins when it finds a match;
   * this only lets sales record what they already know about someone the
   * lookup cannot see (a different personal address, say).
   */
  knownSkidaUser?: boolean
}

/**
 * Result of the read-only check against the consumer Skida `users` collection
 * (same database — consumer and PRO share this project). Derived at read time
 * by awarenessRepository, never persisted on the lead, so it cannot go stale.
 */
export interface ConsumerAwareness {
  email: string
  isConsumerUser: boolean
  /** Year the consumer account was created, when known. */
  since?: string
}

export type ContactWithAwareness = Contact & { awareness?: ConsumerAwareness }

/**
 * Per-guide Skida PRO status. Written by `crmFirstLoginWatcher` from
 * `users` / `skidaProCompanyMembers` — read-only from the CRM UI's perspective.
 */
export interface GuideStatus {
  name: string
  email: string
  signedIn: boolean
  /** ISO date of the first sign-in, or null when the guide has never signed in. */
  firstLoginAt: string | null
}

/**
 * A banner shown at the top of the lead, above everything else.
 *
 * Exists because some context must not be discoverable only by scrolling the
 * activity log — a company that has just lost guides in an avalanche must not
 * receive a cold trial invitation because the rep never scrolled down.
 *
 * `critical` = do not contact at all. `warning` = contact, but read this first.
 * `info` = neutral context, e.g. the customer is already using the product.
 */
export interface LeadFlag {
  level: 'critical' | 'warning' | 'info'
  /** Short label on the banner, e.g. "Do not contact". */
  label: string
  /** The full explanation. */
  text: string
}

/**
 * Sales qualification — three yes/no criteria, scored 0–3.
 *
 * Deliberately coarse. A finer scale invites arguing about whether something is
 * a 6 or a 7; three booleans a rep can answer from a company's website keep the
 * score comparable across the book.
 */
export interface Qualification {
  /** Enough guides for a B2B deal to be worth the effort. */
  hasVolume: boolean
  /** Already uses apps, maps or tracking — less to explain. */
  hasDigitalTools: boolean
  /** High-cost segment, so the seat price is not the obstacle. */
  highSpend: boolean
  comment: string
}

export const EMPTY_QUALIFICATION: Qualification = {
  hasVolume: false,
  hasDigitalTools: false,
  highSpend: false,
  comment: '',
}

export function qualificationScore(q: Qualification): number {
  return [q.hasVolume, q.hasDigitalTools, q.highSpend].filter(Boolean).length
}

export interface FollowUpTask {
  text: string
  /** ISO date (YYYY-MM-DD). */
  dueDate: string
  assignee: string
  doneAt?: string | null
}

/** How a lead was linked to a Skida PRO company, and by whom. */
export interface ProLink {
  companyId: string
  method: 'vat' | 'domain' | 'name' | 'manual'
  by: string
  at: string | null
}

/** A CRM lead — one guiding company in the sales pipeline. Collection: `skidaCrmLeads`. */
export interface Lead {
  /** Where it is, and how precisely — see `GeoPoint`. Written by the geocoder. */
  geo?: GeoPoint
  id: string
  name: string
  region: string
  /** Display name of the owning sales rep. */
  owner: string
  /**
   * The owner's sign-in address — what the external-sales rule matches on.
   *
   * `owner` is a display name and is what every screen shows. A security rule
   * cannot use it: two people called the same thing would share a pipeline, and
   * a rename would silently move somebody's book. This is written wherever the
   * owner is set and is the only field `firestore.rules` trusts.
   *
   * Empty on a lead owned by somebody who is not on the allowlist, and on the
   * ones nobody has claimed — an external caller matching on `''` is refused,
   * which is the safe direction.
   */
  ownerEmail?: string
  /** Stage key — either a `PipelineStage.key` or an outcome key. */
  stage: string
  contacts: Contact[]
  nextTask: FollowUpTask | null
  /** Banners shown above the lead. Ordered most severe first by the repository. */
  flags: LeadFlag[]

  /** ---- Company profile, sales-owned ---- */
  /** Bare host, no scheme — "nordheimadventures.no". */
  website: string
  /** Handle without the @, or a full URL; normalised on write. */
  instagram: string
  facebook: string
  /** Absolute URL. Falls back to initials in the UI when missing or broken. */
  logoUrl: string
  /** Free text — "12 guides", "Multi-guide", "1 (Skida PRO-medlemmer)". */
  teamSize: string
  /**
   * Research notes carried in from the import: tech stack, timing window,
   * provenance. Shown as its own card, separate from the activity log — this is
   * standing context about the company, not a dated event.
   */
  prospectingNotes: string
  /** Lowercase company email domains, used by the Cloud Functions to attribute product events. */
  contactDomains: string[]
  /** Street address, free text. */
  address: string
  /** Country, free text — the table sorts and filters on it. */
  country: string
  qualification: Qualification
  /** Machine-harvested, always sourced. See LeadEnrichment. */
  enrichment: LeadEnrichment
  /** True when a skida.app account sits on this company's email domain. */
  hasProductUsers: boolean
  productUserCount: number

  /**
   * How many seats this deal is for, as the rep understands it — sales-owned.
   *
   * The opportunity's size, typed by a person, and the only seat figure that
   * exists before a company buys anything. Deliberately **not** derived from
   * `teamSize`, which is free text carrying things like "Multi-guide" and
   * "1 (Skida PRO-medlemmer)", nor from `enrichment.teamSize`, which is a
   * harvested guess at headcount. A rep who has spoken to the customer knows
   * something neither of those does.
   *
   * Zero means nobody has said yet, and reads as "not set" rather than as a
   * deal worth no seats — a pipeline chart that treated an unfilled field as a
   * real zero would quietly understate every stage.
   *
   * Distinct from `seatsBought` below in the way that matters: this is what we
   * expect, that is what happened.
   */
  seatsExpected: number

  /** ---- Mirrored from Skida PRO product data by Cloud Functions (read-only here) ---- */
  /** Linked `skidaProCompanies` document id, once the company exists in the product. */
  skidaProCompanyId?: string | null
  /**
   * The link a rep made from the Skida PRO dashboard — 21 Oct 2026.
   *
   * Beside `skidaProCompanyId` rather than in it: that field is written only
   * by the Cloud Functions, and the rules keep it so. This one is the rep's,
   * with how the match was made, so the dashboard can show "linked by VAT
   * number" and a wrong link can be undone without touching the mirror.
   */
  proLink?: ProLink | null
  seatsBought: number
  seatsActive: number
  guides: GuideStatus[]

  createdAt?: string
  updatedAt?: string
  /**
   * When this company was won, ISO.
   *
   * Stamped on the transition into `won` and cleared on the way out — not the
   * same thing as `updatedAt`, which moves whenever anybody edits anything.
   * Null on every company won before 12 Aug 2026, because nothing recorded it
   * until then; the sales goal counts those in its total and leaves them out of
   * the season, with an (i) saying so.
   */
  wonAt?: string | null
}

/** Collection: `skidaCrmActivities`. Note or system event on a lead. */
export interface Activity {
  id: string
  leadId: string
  /** ISO timestamp. */
  at: string
  /** Display name, or "System" for Cloud-Function-generated entries. */
  author: string
  text: string
  /**
   * Who or what wrote the line — see `addNote`.
   *
   * `user` is typed into the note box; `action` is the line another control
   * wrote as a side effect and signed with the operator's name; `system` comes
   * from a function or a script. Only `user` is a note.
   */
  source: 'user' | 'action' | 'system'
}

/** Collection: `skidaCrmUsers`. Doc id is the lowercased email address. */
/** See `CrmUser.role`. */
export type CrmRole = 'admin' | 'sales' | 'externalSales' | 'shareholder' | 'ambassador'

/**
 * Staff see the CRM; the other three see one thing each.
 *
 * The array is the same fact in the shape a Firestore `array-contains-any`
 * wants — `subscribeToTeam` queries with it, and the rule that admits that query
 * names the same two roles.
 */
export const STAFF_ROLES: CrmRole[] = ['admin', 'sales']

export const isStaffRole = (r: CrmRole) => r === 'admin' || r === 'sales'

/** Any staff role at all. A list, because a login may hold several. */
export const hasStaffRole = (roles: CrmRole[]) => roles.some(isStaffRole)

/**
 * The roles on a record, whichever shape it is in.
 *
 * One place that knows about the old single `role`, so the rest of the codebase
 * only ever sees a list. Falls back to `sales` for a row with neither, which is
 * the narrowest staff role rather than nothing — a row on the allowlist is
 * somebody who was deliberately let in.
 */
export function rolesOf(d: { roles?: unknown; role?: unknown }): CrmRole[] {
  const known = (v: unknown): v is CrmRole =>
    v === 'admin' || v === 'sales' || v === 'externalSales' ||
    v === 'shareholder' || v === 'ambassador'
  if (Array.isArray(d.roles)) {
    const list = d.roles.filter(known)
    if (list.length) return list
  }
  return known(d.role) ? [d.role] : ['sales']
}

export const ROLE_LABELS: Record<CrmRole, string> = {
  admin: 'Admin',
  sales: 'Sales',
  externalSales: 'External sales',
  shareholder: 'Shareholder',
  ambassador: 'Ambassador',
}

export interface CrmUser {
  email: string
  displayName: string
  /**
   * Job title, as it should appear in an email signature — "Chief Commercial
   * Officer, Mountainsnow AS / Skida". Merged into the outreach templates
   * alongside the name, so the suggestion a rep opens is already signed by
   * them rather than by a placeholder.
   */
  title?: string
  /**
   * Mobile number and employer, for the digital business card.
   *
   * Not used anywhere else — outreach mail signs with name and title only, and
   * a phone number in an email signature is a different decision from a phone
   * number on a card you hand to someone you have just met. Optional because
   * nobody should be blocked from using the tool for not having filled it in,
   * and the card says plainly when it is missing rather than rendering a blank
   * line.
   */
  phone?: string
  company?: string
  /**
   * Download URL of the profile photo in Cloud Storage.
   *
   * The URL rather than the path, because every place that shows a face wants
   * an `<img src>` and resolving a path to a URL is an async call each of them
   * would otherwise have to make. The token in a Firebase download URL changes
   * when the object is replaced, so storing it here is also what makes a new
   * photo appear immediately instead of after a cached one expires.
   */
  photoUrl?: string
  /**
   * What this login may see — the allowlist row is the whole access model.
   *
   * `admin` and `sales` are staff. The other three are people outside the
   * company who were given a way in on 29 Aug 2026, and each sees exactly one
   * thing:
   *
   *   externalSales  the leads assigned to them, by `Lead.ownerEmail`
   *   shareholder    their own holding, plus the cap table projection
   *   ambassador     their own ambassador record, and their own upload folder
   *
   * The enforcement is in `firestore.rules`, not here. This field is what the
   * rules read, and the UI uses it only to decide which screen to open — a
   * client that lied about its own role would still be refused by the rules.
   */
  /**
   * Every role this login holds — 29 Aug 2026, second pass.
   *
   * It was one role, and one role was wrong: somebody can be an external
   * salesperson *and* a shareholder, and the register has staff in it too. A
   * single field forced a choice between two true things and would have been
   * worked around by giving people two logins, which is worse in every way —
   * two passwords, two audit trails, and an allowlist that no longer says who
   * anybody is.
   *
   * Access is the **union**: holding `sales` and `shareholder` means the CRM and
   * your own holding, not some negotiated middle. Nothing here can therefore
   * take access away, which is the property that makes a list of roles safe to
   * reason about.
   */
  roles: CrmRole[]
  /**
   * The single role this used to be. Read, never written.
   *
   * Documents written before the change carry it, and `firestore.rules` falls
   * back to it too, so the migration is not a flag day — a row that has not been
   * rewritten keeps working exactly as it did.
   */
  role?: CrmRole
  /**
   * Which record an outside role *is*, set by an admin.
   *
   * A link rather than a match on the email field of the record: anybody who
   * could get an address onto a shareholder record could otherwise read it.
   * Empty for staff.
   */
  shareholderId?: string
  /** `skidaCrmPeople` id, for an ambassador login. */
  ambassadorId?: string
  disabled?: boolean
  /**
   * Epoch ms of the last sign-in, copied from Firebase Auth by
   * `scripts/syncStaffUsers.ts`. Auth is the only place that knows, and only
   * the Admin SDK can read it — so it is synced in rather than looked up.
   */
  lastSignInAt?: number | null
}

/**
 * ---------------------------------------------------------------------------
 * Harvested enrichment (Lead Harvest & Auto-Scoring spec v1.0)
 * ---------------------------------------------------------------------------
 *
 * Everything below is machine-collected and must be *sourced*. The rule the
 * spec leads with is the one that matters most here: never fabricate. A field
 * with no `evidence` entry behind it is either absent or marked `verify`.
 *
 * Kept in one nested map rather than spread across the lead document so it is
 * obvious at a glance which fields a human typed and which a harvester wrote —
 * and so a bad run can be cleared without touching sales-owned data.
 */

/** Where one harvested field came from. Unsourced means unscored. */
export interface LeadEvidence {
  /** The enrichment field this supports, e.g. "teamSize". */
  field: string
  url: string
  /** ISO date the page was actually loaded. */
  retrievedAt: string
}

export type LeadTier = 'A' | 'B' | 'C' | 'D'
export type EnrichmentConfidence = 'high' | 'medium' | 'low'

/** How the company sells: rising order of digital investment. */
export type BookingMode = 'none' | 'email-form' | 'webshop' | 'booking-saas' | 'own-system'

export type EntityType = 'company' | 'sole-trader' | 'school' | 'association' | 'federation'

export interface LeadEnrichment {
  /** Composite 0–100: round(4·size + 3·digital + 3·power), then the overrides. */
  leadScore: number | null
  tier: LeadTier | null
  /** Share of the three axes carrying a hard sourced signal. */
  confidence: EnrichmentConfidence | null
  axisSize: number | null
  axisDigital: number | null
  axisPower: number | null

  /** ---- Size & volume ---- */
  /** Best estimate of guides. Drives `estAcvEur` — headcount *is* deal size. */
  teamSize: number | null
  teamSizeSource: string
  guidesNamed: number | null
  guidesClaimed: number | null
  productCount: number | null
  hiringNow: boolean
  operatingYears: number | null
  entityType: EntityType | null
  /** teamSize × €360, the confirmed annual seat price. Tiebreaker on equal scores. */
  estAcvEur: number | null

  /**
   * ---- Company register (proff.no, allabolag.se) ----
   *
   * Registry facts, which are a different kind of evidence from a website: the
   * company filed them, under obligation, rather than writing them for a
   * customer to read.
   *
   * `employees` is emphatically **not** `teamSize`. Guiding runs on freelancers:
   * Narvik Mountain Guides files 1–4 employees while its site describes a team
   * of IFMGA guides, and Active Ski Travel files 4 employees against 27.5m SEK
   * of revenue — a tour operator that buys its guiding in. Where the two agree,
   * as at Åreguiderna (8 and 8), that is corroboration worth having. Where they
   * disagree, the disagreement is the finding.
   */
  orgNumber: string
  /** Payroll headcount as filed. A band like "1–4" is kept verbatim. */
  employees: string
  employeesYear: number | null
  revenueEur: number | null
  revenueYear: number | null
  /** Managing director as registered — who to ask for, not an address. */
  ceo: string
  foundedYear: number | null

  /**
   * Instagram handle without the @, and the follower count when it was read.
   *
   * Reach is evidence of *marketing* effort, not of operational tooling — a
   * guide with sixty thousand followers may still plan the day on paper. It
   * nudges the digital axis rather than driving it, and the count is a snapshot
   * that ages, so it carries the date it was taken.
   */
  instagram: string
  instagramFollowers: number | null
  instagramVerified: boolean

  /**
   * ISO date the company's own website was last opened and read end to end.
   *
   * Deliberately separate from what the reading produced. A site with no prices
   * and no booking — an association, a placeholder, a company behind an expired
   * certificate — yields nothing, and inferring "unread" from an empty booking
   * mode would put it back at the top of the queue every time.
   */
  siteReadAt: string

  /** ---- Digital maturity ---- */
  bookingMode: BookingMode | null
  bookingVendor: string
  mapTools: string[]
  safetyTools: string[]
  waiverDigital: boolean
  ownAppOrPortal: boolean
  cms: string
  socialLastPostDays: number | null
  /**
   * Their own stack is harder to displace. Drives the pitch angle, never the
   * score — the spec is explicit that a 9–10 on digital maturity is not a
   * penalty.
   */
  displacementRisk: boolean
  /** Booking SaaS covers neither planning nor safety, so it is a partner. */
  stackComplementary: boolean

  /** ---- Purchasing power ---- */
  pricePerPersonDayEur: number | null
  priceCurrencyOriginal: string
  priceSourceUrl: string
  productTier: string[]
  luxuryMarkers: string[]
  certLevel: string
  clientType: string[]
  countryCostTier: 'high' | 'mid' | 'low' | null

  /** ---- Sales context ---- */
  /** ≤40 words, every clause traceable to a harvested field. The 10-second read. */
  snapshot: string
  /** One sentence, tied to an actual tech finding — never a generic line. */
  hook: string
  /** "Aug–Oct" northern · "Apr–Jun" course-heavy · "Feb–May" southern. */
  contactWindow: string
  baseTown: string
  latitude: number | null
  longitude: number | null
  geoStatus: 'town' | 'address' | 'region' | 'unknown' | null

  /** ---- Provenance ---- */
  evidence: LeadEvidence[]
  /** Something here was inferred rather than read. A human must confirm it. */
  verify: boolean
  /** The site refused to load; scored from registries and social only. */
  siteBlocked: boolean
  /** ISO timestamp of the last harvest run that touched this lead. */
  enrichedAt: string | null
  /** Which run wrote it, e.g. "daily 2026-08-07". */
  source: string
}

export const EMPTY_ENRICHMENT: LeadEnrichment = {
  leadScore: null, tier: null, confidence: null,
  axisSize: null, axisDigital: null, axisPower: null,
  teamSize: null, teamSizeSource: '', guidesNamed: null, guidesClaimed: null,
  productCount: null, hiringNow: false, operatingYears: null, entityType: null,
  estAcvEur: null,
  orgNumber: '', employees: '', employeesYear: null,
  revenueEur: null, revenueYear: null, ceo: '', foundedYear: null,
  instagram: '', instagramFollowers: null, instagramVerified: false,
  siteReadAt: '',
  bookingMode: null, bookingVendor: '', mapTools: [], safetyTools: [],
  waiverDigital: false, ownAppOrPortal: false, cms: '', socialLastPostDays: null,
  displacementRisk: false, stackComplementary: false,
  pricePerPersonDayEur: null, priceCurrencyOriginal: '', priceSourceUrl: '',
  productTier: [], luxuryMarkers: [], certLevel: '', clientType: [],
  countryCostTier: null,
  snapshot: '', hook: '', contactWindow: '', baseTown: '',
  latitude: null, longitude: null, geoStatus: null,
  evidence: [], verify: false, siteBlocked: false, enrichedAt: null, source: '',
}

/** Annual price of one Skida PRO seat, billed a year in advance. */
export const SEAT_PRICE_EUR = 360

export const TIER_STYLES: Record<LeadTier, { color: string; bg: string }> = {
  A: { color: '#3aa52f', bg: '#eaf7e7' },
  B: { color: '#3456da', bg: '#eaefff' },
  C: { color: '#e8a52f', bg: '#fff3d8' },
  D: { color: '#aeb1b4', bg: '#f1f2f3' },
}

export function tierFor(score: number): LeadTier {
  if (score >= 75) return 'A'
  if (score >= 55) return 'B'
  if (score >= 35) return 'C'
  return 'D'
}
